import React, { useMemo } from 'react'
import { Button, ThemeProvider, Typography, createTheme, makeStyles, useTheme } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useHistory } from 'react-router-dom'
import PaintScoutIcon from 'src/public/images/PaintScout_Icon_White.svg'
import PaintScoutWordmark from 'src/public/images/PaintScout_Wordmark_White.svg'

// Quoted: a production build inlines these small SVGs as `data:image/svg+xml,...` strings that
// contain single quotes, which an unquoted url() cannot hold. The browser then drops the mask and
// paints a solid block of colour (ENG-2289).
const maskedImage = (src: string, color: string) => ({
  backgroundColor: color,
  maskImage: `url("${src}")`,
  WebkitMaskImage: `url("${src}")`,
  maskSize: 'contain',
  WebkitMaskSize: 'contain',
  maskRepeat: 'no-repeat',
  WebkitMaskRepeat: 'no-repeat',
  maskPosition: 'center',
  WebkitMaskPosition: 'center'
})

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4, 2),
    boxSizing: 'border-box',
    backgroundColor: theme.palette.grey[100]
  },
  card: {
    width: '100%',
    maxWidth: 480,
    padding: theme.spacing(5, 4),
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    textAlign: 'center'
  },
  // Admin's white icon and wordmark (NavDrawer), at 2.5× its size. The SVGs are white, so each is
  // a CSS mask over a block of colour: the icon in PaintScout blue, the wordmark in black text.
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: 17,
    marginBottom: theme.spacing(1)
  },
  logoIcon: {
    ...maskedImage(PaintScoutIcon, theme.palette.primary.main),
    width: 51,
    height: 60
  },
  logoWordmark: {
    ...maskedImage(PaintScoutWordmark, theme.palette.text.primary),
    width: 275,
    height: 45,
    marginBottom: 10
  }
}))

/** Whether the billing contact is one of the checkout's new users, who has no password yet. */
export type CheckoutOutcome = 'new' | 'existing'

/**
 * The PaintScout-blue theme for the public checkout pages. The theme before login has a grey
 * primary (getTheme), and a company's own colour only loads after login. These pages belong to
 * PaintScout, so their primary is PaintScout blue.
 */
export function CheckoutPageTheme({ children }: { children: React.ReactNode }) {
  const outer = useTheme<Theme>()
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          primary: { main: outer.paintscout.palette.brand.Blue.Blue55 },
          secondary: { main: '#FFFFFF' }
        }
      }),
    [outer]
  )

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>
}

/**
 * Fetches the logo images ahead of time. A CSS mask image loads only when its element first renders,
 * so without this the success card after an embedded checkout (HostedCheckout) waits for them. A
 * production build inlines both SVGs, so this matters only where they are served as files (the
 * dev server).
 */
export function preloadCheckoutLogo() {
  for (const src of [PaintScoutIcon, PaintScoutWordmark]) {
    const image = new Image()
    image.src = src
  }
}

export function CheckoutLogo() {
  const classes = useStyles({})
  return (
    <div className={classes.logo} role="img" aria-label="PaintScout">
      <span className={classes.logoIcon} />
      <span className={classes.logoWordmark} />
    </div>
  )
}

/**
 * Shown by the checkout page (HostedCheckout) once Chargebee reports payment (ENG-2265).
 *
 * A new user has no password yet: the createCheckoutUsers task creates the account after the
 * subscription_created webhook, and Auth0 emails the set-password link. So that case shows no
 * login button.
 */
export function CheckoutCompleteCard({ outcome }: { outcome: CheckoutOutcome }) {
  const classes = useStyles({})
  const history = useHistory()

  return (
    <div className={classes.root}>
      <div className={classes.card}>
        <CheckoutLogo />
        {outcome === 'new' && (
          <>
            <Typography variant="h2">Your subscription is active</Typography>
            <Typography variant="body1">
              We are setting up your account. In a few minutes you will get an email with a link to set your password.
              Check your spam folder.
            </Typography>
            <SupportLine />
          </>
        )}
        {outcome === 'existing' && (
          <>
            <Typography variant="h2">Your subscription is active</Typography>
            {/* A real button, not href: as an <a> it took the global link styles. "/" sends a
                signed-out user to log in. */}
            <Button variant="contained" color="primary" onClick={() => history.push('/')}>
              Go to PaintScout
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

export function SupportLine() {
  return (
    <Typography variant="body2" color="textSecondary">
      Questions? Email <a href="mailto:support@paintscout.com">support@paintscout.com</a>.
    </Typography>
  )
}
